import { $t, type MessageFormatter } from '@repo/i18n'
import { DateTime, type DurationLike } from 'luxon'
import { locale } from 'svelte-i18n'
import { get } from 'svelte/store'

export function dateTimeToUrl(source: DateTime) {
	return source.toISO()
}

export function dateToUrl(source: DateTime) {
	return source.toISODate()
}

export function dateTimeFromUrl(source: string) {
	return DateTime.fromISO(source)
}

export function getCurrentDateTime(): DateTime<true> {
	return DateTime.now()
}

export function getWeeks(initialDate: DateTime = getCurrentDateTime()): DateTime<boolean>[][] {
	const startDay = initialDate.startOf('month').startOf('week')
	const endDay = initialDate.endOf('month').endOf('week')

	let date = startDay

	const weeks: Array<Array<DateTime>> = []
	while (date.startOf('day') <= endDay.startOf('day')) {
		weeks.push(
			Array(7)
				.fill(0)
				.map(() => {
					const currentDate = date
					date = date.plus({ days: 1 })
					return currentDate
				}),
		)
	}

	return weeks
}

export function getHumanReadableDate(date?: DateTime) {
	if (date) {
		return date.toLocaleString(
			{ day: '2-digit', month: '2-digit', year: 'numeric' },
			{ locale: get(locale) },
		)
	}
}

export function getHumanReadableDateTime(date?: DateTime) {
	if (date) {
		return date.toLocaleString(
			{
				day: '2-digit',
				month: '2-digit',
				year: 'numeric',
				hour: '2-digit',
				minute: '2-digit',
				second: '2-digit',
			},
			{ locale: get(locale) },
		)
	}
}

export function findSelectedPreset(start: DateTime, end: DateTime, allowedPresets: Presets[]) {
	for (const presetKey of allowedPresets) {
		const preset = presets[presetKey]
		const presetStart = preset.start()
		const presetEnd = preset.end()

		if (presetStart && presetEnd && start && end) {
			if (start.equals(presetStart) && end.equals(presetEnd)) {
				return presets[presetKey]
			}
		}
	}

	return null
}

export function formatDateRange(
	start: DateTime,
	end: DateTime,
	allowedPresets: Presets[],
	$_: MessageFormatter,
) {
	if (start === null) {
		return null
	}

	const selectedPreset = findSelectedPreset(start, end, allowedPresets)
	if (selectedPreset) {
		return $_(selectedPreset.title)
	}

	if (end && start.equals(start.startOf('year')) && end.equals(end.endOf('year'))) {
		return start.toLocaleString({ year: 'numeric' }, { locale: get(locale) })
	}

	if (end && start.equals(start.startOf('month')) && end.equals(end.endOf('month'))) {
		return start.toLocaleString({ month: 'short', year: 'numeric' }, { locale: get(locale) })
	}

	const formattedStart = getHumanReadableDate(start)
	const formattedEnd = getHumanReadableDate(end)

	if (formattedStart === formattedEnd || !end) {
		return formattedStart
	}

	return `${formattedStart} – ${formattedEnd}`
}

export interface Preset {
	title: string
	start: () => DateTime | null
	end: () => DateTime | null
	periodDiff?: DurationLike
}

export enum Presets {
	today = 'today',
	yesterday = 'yesterday',
	last7days = 'last7days',
	last30days = 'last30days',
	thisMonth = 'thisMonth',
	previousMonth = 'previousMonth',
	thisYear = 'thisYear',
	lastYear = 'lastYear',
	previousYear = 'previousYear',
	anytime = 'anytime',
	fakeAnytime = 'fakeAnytime',
}

type PresetSet = { [key in Presets]: Preset }

export const presets: PresetSet = {
	[Presets.today]: {
		title: $t('datepicker.preset.today'),
		start: () => getCurrentDateTime().startOf('day'),
		end: () => getCurrentDateTime().endOf('day'),
	},
	[Presets.yesterday]: {
		title: $t('datepicker.preset.yesterday'),
		start: () => getCurrentDateTime().minus({ days: 1 }).startOf('day'),
		end: () => getCurrentDateTime().minus({ days: 1 }).endOf('day'),
	},
	[Presets.last7days]: {
		title: $t('datepicker.preset.last7Days'),
		start: () => getCurrentDateTime().minus({ days: 7 }).startOf('day'),
		end: () => getCurrentDateTime().endOf('day'),
	},
	[Presets.last30days]: {
		title: $t('datepicker.preset.last30Days'),
		start: () => getCurrentDateTime().minus({ days: 30 }).startOf('day'),
		end: () => getCurrentDateTime().endOf('day'),
	},
	[Presets.thisMonth]: {
		title: $t('datepicker.preset.thisMonth'),
		start: () => getCurrentDateTime().startOf('month'),
		end: () => getCurrentDateTime().endOf('month'),
		periodDiff: { months: 1 },
	},
	[Presets.previousMonth]: {
		title: $t('datepicker.preset.previousMonth'),
		start: () => getCurrentDateTime().minus({ months: 1 }).startOf('month'),
		end: () => getCurrentDateTime().minus({ months: 1 }).endOf('month'),
		periodDiff: { months: 1 },
	},
	[Presets.lastYear]: {
		title: $t('datepicker.preset.lastYear'),
		start: () => getCurrentDateTime().minus({ days: 365 }).startOf('day'),
		end: () => getCurrentDateTime().endOf('day'),
	},
	[Presets.thisYear]: {
		title: $t('datepicker.preset.thisYear'),
		start: () => getCurrentDateTime().startOf('year'),
		end: () => getCurrentDateTime().endOf('year'),
		periodDiff: { years: 1 },
	},
	[Presets.previousYear]: {
		title: $t('datepicker.preset.previousYear'),
		start: () => getCurrentDateTime().minus({ years: 1 }).startOf('year'),
		end: () => getCurrentDateTime().minus({ years: 1 }).endOf('year'),
		periodDiff: { years: 1 },
	},
	[Presets.anytime]: {
		title: $t('datepicker.preset.anyTime'),
		start: () => null,
		end: () => null,
	},
	[Presets.fakeAnytime]: {
		title: $t('datepicker.preset.anyTime'),
		start: () => dateTimeFromUrl('2022-01-01T00:00Z'),
		end: () => dateTimeFromUrl('3022-01-01T00:00Z'),
	},
}
